import { WidgetSpec } from "@growth/schema";
import { Button, Field, Input, Textarea } from "@growth/ui";
import * as Dialog from "@radix-ui/react-dialog";
import { LoaderCircle } from "lucide-react";
import { type FormEvent, useState } from "react";

import { generateChart } from "../../lib/ai";
import { apiErrorMessage } from "../../lib/api-error";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (spec: WidgetSpec) => void;
};

/**
 * The chart / dashboard maker. With pasted data the numbers must come with their source; without
 * data the agent researches it. Either way the server rejects numbers that aren't in the data.
 */
export const ChartDialog = ({ open, onOpenChange, onInsert }: Props) => {
  const [request, setRequest] = useState("");
  const [data, setData] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setRequest("");
    setData("");
    setSourceLabel("");
    setSourceUrl("");
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (data.trim() && (!sourceLabel.trim() || !sourceUrl.startsWith("https://"))) {
      setError("Pasted data needs its source: a label and an https:// link.");
      return;
    }
    setBusy(true);
    try {
      const { spec } = await generateChart({
        request,
        ...(data.trim()
          ? { data, source: { label: sourceLabel.trim(), url: sourceUrl.trim() } }
          : {}),
      });
      onInsert(spec);
      reset();
      onOpenChange(false);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Couldn't build the chart"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-line bg-white p-6 shadow-xl">
          <Dialog.Title className="font-serif text-lg text-ink">
            Add a chart or dashboard
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted">
            Describe what readers should see. Paste your own numbers with their source, or leave the
            data empty and the agent will research it. Every number shown links to its source.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <Field label="What should it show?">
              {(props) => (
                <Textarea
                  {...props}
                  required
                  minLength={5}
                  rows={3}
                  value={request}
                  placeholder="e.g. Graduate unemployment in Australia over the last five years"
                  onChange={(e) => setRequest(e.target.value)}
                />
              )}
            </Field>
            <Field label="Data (optional)" hint="Paste a small table or list of figures.">
              {(props) => (
                <Textarea
                  {...props}
                  rows={4}
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              )}
            </Field>
            {data.trim() && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Source label">
                  {(props) => (
                    <Input
                      {...props}
                      value={sourceLabel}
                      placeholder="ABS, Labour Force, Sept 2026"
                      onChange={(e) => setSourceLabel(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Source link">
                  {(props) => (
                    <Input
                      {...props}
                      type="url"
                      value={sourceUrl}
                      placeholder="https://…"
                      onChange={(e) => setSourceUrl(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            )}
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <Button type="button" variant="secondary" disabled={busy}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={busy}>
                {busy && <LoaderCircle className="size-4 animate-spin" />}
                {busy ? (data.trim() ? "Building…" : "Researching and building…") : "Make it"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
